import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/ampa-receptors-ketamine-and-tms — PROMPT 9 (bonus)
 * "AMPA" is also a brain receptor. Google mixes the two meanings. Ketamine
 * and Spravato® are thought to work partly through AMPA receptors, and we
 * offer ketamine, Spravato® and Ampa One. One page owns both meanings.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "What is AMPA?",
    answer:
      "AMPA has two meanings in depression care. AMPA receptors are glutamate receptors in the brain that help neurons strengthen their connections (neuroplasticity). Ampa One is an unrelated TMS device that shares the name. Research suggests ketamine and esketamine (Spravato®) relieve depression partly by boosting AMPA-receptor signaling. Both approaches aim to help the brain rewire.",
  },
  {
    question: "What do AMPA receptors have to do with depression?",
    answer:
      "AMPA receptors are glutamate receptors that help neurons strengthen their connections — a process called neuroplasticity that depression can impair. Research suggests ketamine and esketamine (Spravato®) relieve depression partly by boosting AMPA-receptor signaling, which helps the brain form new connections. This is one theory for why glutamate-based treatments can work when monoamine antidepressants have not.",
  },
  {
    question: "Is Ampa TMS related to AMPA receptors?",
    answer:
      "No. Ampa One is a TMS device that shares the name 'Ampa' with the AMPA receptor, but they are unrelated. Ampa One delivers magnetic stimulation to the prefrontal cortex; AMPA receptors are proteins on neurons. The name overlap is a coincidence. Both approaches aim to help the brain rewire, which is why some clinics, including ours, offer both.",
  },
  {
    question: "Can I do Spravato and TMS together?",
    answer:
      "Combining Spravato® (esketamine) and TMS is a physician decision based on your history and diagnosis. There is no rule against it, and some patients benefit from a combined approach, but it is not right for everyone. Our psychiatrists decide based on your situation. See [[Spravato vs ketamine|/spravato-vs-ketamine]] and [[TMS vs Spravato|/learn/tms-vs-spravato]].",
  },
];

export const pageAmpaReceptorsKetamineAndTms = makeArticle({
  slug: "ampa-receptors-ketamine-and-tms",
  title: "AMPA Receptors, Ketamine and Ampa One TMS: What's the Connection?",
  description:
    "AMPA receptors are glutamate receptors linked to neuroplasticity; ketamine and Spravato® may work partly through them. Ampa One TMS shares the name but is unrelated. We offer both approaches.",
  canonical: "/learn/ampa-receptors-ketamine-and-tms",
  h1: '"AMPA" in Depression Care: The Brain Receptor vs the TMS Device',
  breadcrumbLabel: "AMPA Receptors, Ketamine & Ampa One TMS",
  shortAnswer:
    "AMPA receptors are glutamate receptors in the brain that help neurons strengthen their connections (neuroplasticity). Research suggests ketamine and esketamine (Spravato®) relieve depression partly by boosting AMPA-receptor signaling. Ampa One is an unrelated TMS device that shares the name. Both approaches aim to help the brain rewire, which is why some clinics, including ours, offer both. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "AMPA receptors are glutamate receptors that help neurons strengthen connections (neuroplasticity).",
    "Ketamine and esketamine (Spravato®) may relieve depression partly by boosting AMPA-receptor signaling.",
    "Ampa One is a TMS device that shares the name 'Ampa' but is unrelated to the receptor.",
    "Both approaches aim to help the brain rewire, which is why some clinics offer both.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "AMPA receptors are glutamate receptors in the brain that help neurons strengthen their connections (neuroplasticity). Research suggests ketamine and esketamine (Spravato®) relieve depression partly by boosting AMPA-receptor signaling. Ampa One is an unrelated TMS device that shares the name. Both approaches aim to help the brain rewire, which is why some clinics, including ours, offer both.",
      ],
    },
    {
      heading: "What AMPA receptors do",
      paragraphs: [
        "AMPA receptors are a type of glutamate receptor — the most common excitatory receptor in the brain. When glutamate binds an AMPA receptor, the neuron is more likely to fire, and repeated activation helps strengthen the connection between two neurons. That strengthening is called neuroplasticity, and it is the basis of learning and adaptation. Depression is associated with reduced neuroplasticity in key brain regions, so treatments that restore it are a major focus of current research.",
      ],
    },
    {
      heading: "How ketamine and Spravato® involve AMPA receptors",
      paragraphs: [
        "Ketamine and esketamine (Spravato®) are thought to relieve depression partly by boosting AMPA-receptor signaling. Ketamine blocks the NMDA receptor, which triggers a cascade that increases AMPA-receptor activity and, in turn, promotes neuroplasticity. This is one leading theory for why glutamate-based treatments can work quickly in patients who have not responded to monoamine antidepressants. See [[Spravato vs ketamine|/spravato-vs-ketamine]].",
      ],
    },
    {
      heading: "What Ampa One TMS is",
      paragraphs: [
        "Ampa One is an FDA-cleared transcranial magnetic stimulation (TMS) device for major depressive disorder in adults who have not improved enough on antidepressants. It uses camera-guided targeting with a pre-printed cap and delivers magnetic pulses to the left dorsolateral prefrontal cortex to stimulate that region. It shares the name 'Ampa' with the AMPA receptor but is unrelated. See [[Ampa One TMS in Florida|/ampa]].",
      ],
    },
    {
      heading: "Can ketamine/Spravato® and TMS be combined?",
      paragraphs: [
        "Combining ketamine or Spravato® with TMS is a physician decision based on your history and diagnosis. There is no rule against it, and some patients benefit from a combined approach that targets depression from more than one angle, but it is not right for everyone. Our psychiatrists decide based on your situation. See [[TMS vs Spravato|/learn/tms-vs-spravato]] and [[Spravato vs ketamine|/spravato-vs-ketamine]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Duman RS, Aghajanian GK. Synaptic dysfunction in depression: potential therapeutic targets. Science. 2012;338(6103):68-72.",
      url: "https://www.science.org/doi/10.1126/science.1222939",
    },
    {
      citation:
        "Ampa Inc. Ampa One — FDA-cleared TMS system for major depressive disorder.",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "Spravato® (Esketamine)",
      href: "/services/spravato",
      description:
        "FDA-approved nasal spray for treatment-resistant depression",
    },
    {
      title: "Spravato vs Ketamine",
      href: "/spravato-vs-ketamine",
      description: "How the two compare",
    },
    {
      title: "TMS vs Spravato",
      href: "/learn/tms-vs-spravato",
      description: "Comparing TMS and esketamine for depression",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "AMPA receptors", href: "#what-ampa-receptors-do" },
    {
      label: "Ketamine & Spravato®",
      href: "#how-ketamine-and-spravato-involve-ampa-receptors",
    },
    { label: "Ampa One TMS", href: "#what-ampa-one-tms-is" },
    {
      label: "Combining them",
      href: "#can-ketaminespravato-and-tms-be-combined",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Talk through your options with our team, free",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
